import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConsumerAuth } from '../../context/ConsumerAuthContext';
import axios from 'axios';
import { 
  FileText, Check, Shield, Clock, Star, Zap, Building2, 
  ChevronRight, AlertCircle, DollarSign, Users, TrendingUp,
  CheckCircle, Info, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASReportOrder = () => {
  const navigate = useNavigate();
  const { consumer, token, loading: authLoading } = useConsumerAuth();
  const [tiers, setTiers] = useState([]);
  const [addons, setAddons] = useState([]);
  const [disclosures, setDisclosures] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [selectedTier, setSelectedTier] = useState('standard');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [step, setStep] = useState(1); // 1: Select Tier, 2: Add-ons, 3: Disclosures, 4: Checkout
  const [disclosureAcknowledged, setDisclosureAcknowledged] = useState(false);
  const [termsAcknowledged, setTermsAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, disclosuresRes] = await Promise.all([
        axios.get(`${API}/pas/tiers`),
        axios.get(`${API}/pas/disclosures`)
      ]);
      setTiers(tiersRes.data.tiers || []);
      setAddons(tiersRes.data.addons || []);
      setDisclosures(disclosuresRes.data || {});
    } catch (err) {
      console.error('Failed to fetch PAS data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const calculateTotal = () => {
    const tier = tiers.find(t => t.tier_id === `tier_${selectedTier}`);
    const basePrice = tier?.price || 0;
    const addonTotal = selectedAddons.reduce((sum, addonId) => {
      const addon = addons.find(a => a.addon_id === addonId);
      return sum + (addon?.price || 0);
    }, 0);
    return basePrice + addonTotal;
  };

  const handleSubmit = async () => {
    // Check authentication before submitting
    if (!token) {
      // Save current selections to session storage for retrieval after login
      sessionStorage.setItem('pas_order_state', JSON.stringify({
        selectedTier,
        selectedAddons,
        disclosureAcknowledged,
        termsAcknowledged
      }));
      navigate('/beth-finance/login?redirect=/beth-finance/pas/order');
      return;
    }

    if (!disclosureAcknowledged || !termsAcknowledged) {
      alert('Please acknowledge all disclosures to continue.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/pas/order`, {
        tier: selectedTier,
        addons: selectedAddons,
        first_name: consumer?.first_name || '',
        last_name: consumer?.last_name || '',
        email: consumer?.email || '',
        phone: consumer?.phone || '',
        address: consumer?.address || '',
        city: consumer?.city || '',
        state: consumer?.state || '',
        zip_code: consumer?.zip_code || '',
        debts: [],
        annual_income: consumer?.annual_income || 0,
        employment_status: consumer?.employment_status || 'employed',
        disclosure_acknowledged: disclosureAcknowledged,
        terms_acknowledged: termsAcknowledged
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        navigate(`/beth-finance/pas/success?order_id=${response.data.order_id}`);
      }
    } catch (err) {
      console.error('Failed to create order:', err);
      if (err.response?.status === 401) {
        navigate('/beth-finance/login?redirect=/beth-finance/pas/order');
      } else {
        alert('Failed to create order. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const tierIcons = {
    standard: <FileText className="w-8 h-8" />,
    advanced: <TrendingUp className="w-8 h-8" />,
    institutional: <Building2 className="w-8 h-8" />
  };

  const tierColors = {
    standard: 'border-blue-500 bg-blue-50',
    advanced: 'border-purple-500 bg-purple-50',
    institutional: 'border-amber-500 bg-amber-50'
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#012169] text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center font-bold text-xl">B</div>
              <span className="text-xl font-bold">BETH Finance</span>
            </div>
            <button 
              onClick={() => navigate('/beth-finance/dashboard')}
              className="text-white/80 hover:text-white"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#012169] mb-2">Credit Enhancement Report</h1>
          <p className="text-gray-600">PAS - Payment Assurance System</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-[#012169] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 4 && <div className={`w-20 h-1 ${step > s ? 'bg-[#012169]' : 'bg-gray-200'}`}></div>}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Tier */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-center text-gray-900">Select Your Report Tier</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {tiers.map((tier) => {
                const tierId = tier.tier_id.replace('tier_', '');
                const isSelected = selectedTier === tierId;
                
                return (
                  <div
                    key={tier.tier_id}
                    data-testid={`tier-card-${tierId}`}
                    onClick={() => setSelectedTier(tierId)}
                    className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all ${
                      isSelected 
                        ? `${tierColors[tierId]} border-2 shadow-lg scale-[1.02]` 
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {tierId === 'advanced' && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                        MOST POPULAR
                      </div>
                    )}
                    
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 ${
                      tierId === 'standard' ? 'bg-blue-100 text-blue-600' :
                      tierId === 'advanced' ? 'bg-purple-100 text-purple-600' :
                      'bg-amber-100 text-amber-600'
                    }`}>
                      {tierIcons[tierId]}
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tier.name}</h3>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-[#012169]">${tier.price}</span>
                      {tier.price_range && (
                        <span className="text-sm text-gray-500 ml-2">{tier.price_range}</span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                    
                    <ul className="space-y-2">
                      {tier.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>Delivery in {tier.delivery_days} business days</span>
                      </div>
                    </div>
                    
                    {isSelected && (
                      <div className="absolute top-4 right-4">
                        <div className="w-6 h-6 bg-[#012169] rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-center">
              <button
                data-testid="continue-to-addons"
                onClick={() => setStep(2)}
                className="px-8 py-3 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                Continue <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Add-ons */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-center text-gray-900">Optional Add-ons</h2>
            <p className="text-center text-gray-600">Enhance your report with additional services</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {addons.map((addon) => {
                const isSelected = selectedAddons.includes(addon.addon_id);
                
                return (
                  <div
                    key={addon.addon_id}
                    onClick={() => toggleAddon(addon.addon_id)}
                    className={`rounded-xl border-2 p-4 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-[#012169] bg-blue-50' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-[#012169] text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {addon.addon_id === 'addon_rush' && <Zap className="w-5 h-5" />}
                        {addon.addon_id === 'addon_debt' && <FileText className="w-5 h-5" />}
                        {addon.addon_id === 'addon_annual' && <Star className="w-5 h-5" />}
                        {addon.addon_id === 'addon_lender' && <Building2 className="w-5 h-5" />}
                        {addon.addon_id === 'addon_portfolio' && <Users className="w-5 h-5" />}
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-[#012169] bg-[#012169]' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 mb-1">{addon.name}</h4>
                    <p className="text-sm text-gray-600 mb-2">{addon.description}</p>
                    <p className="font-bold text-[#012169]">
                      {addon.price_label || `$${addon.price}`}
                    </p>
                  </div>
                );
              })}
            </div>
            
            {/* Price Summary */}
            <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Report Tier:</span>
                <span className="font-semibold text-gray-900">{tiers.find(t => t.tier_id === `tier_${selectedTier}`)?.name}</span>
              </div>
              {selectedAddons.length > 0 && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Add-ons ({selectedAddons.length}):</span>
                  <span className="font-semibold text-gray-900">
                    +${selectedAddons.reduce((sum, id) => sum + (addons.find(a => a.addon_id === id)?.price || 0), 0)}
                  </span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between items-center">
                <span className="font-bold text-gray-900">Total:</span>
                <span className="text-2xl font-bold text-[#012169]">${calculateTotal()}</span>
              </div>
            </div>
            
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="px-8 py-3 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                Continue <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Disclosures */}
        {step === 3 && (
          <div className="space-y-6 max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-center text-gray-900">Important Disclosures</h2>
            
            {/* Main Disclosure */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-amber-900 mb-2">Important Disclosure</h3>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    {disclosures.main_disclosure}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Advance Fee Compliance */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-blue-900 mb-2">Advance-Fee Compliance Statement</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    {disclosures.advance_fee_compliance}
                  </p>
                </div>
              </div>
            </div>
            
            {/* What This Is / Is Not */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" /> What This IS
                </h4>
                <ul className="space-y-2">
                  {disclosures.what_this_is?.map((item, idx) => (
                    <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" /> What This Is NOT
                </h4>
                <ul className="space-y-2">
                  {disclosures.what_this_is_not?.map((item, idx) => (
                    <li key={idx} className="text-sm text-red-800 flex items-start gap-2">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">✗</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Acknowledgment Checkboxes */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={disclosureAcknowledged}
                  onChange={(e) => setDisclosureAcknowledged(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-[#012169] focus:ring-[#012169]"
                />
                <span className="text-sm text-gray-700">
                  I have read and understand the above disclosures. I acknowledge that this service provides 
                  analysis and reporting only, with no guarantees of financial outcomes.
                </span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAcknowledged}
                  onChange={(e) => setTermsAcknowledged(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-[#012169] focus:ring-[#012169]"
                />
                <span className="text-sm text-gray-700">
                  I agree to the Terms of Service and understand that fees are charged for analytical and 
                  reporting services only.
                </span>
              </label>
            </div>
            
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(4)}
                disabled={!disclosureAcknowledged || !termsAcknowledged}
                className={`px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors ${
                  disclosureAcknowledged && termsAcknowledged
                    ? 'bg-[#012169] hover:bg-[#01337d] text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Checkout */}
        {step === 4 && (
          <div className="space-y-6 max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-center text-gray-900">Review & Pay</h2>
            
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Order Summary */}
              <div className="p-6 border-b border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4">Order Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {tiers.find(t => t.tier_id === `tier_${selectedTier}`)?.name}
                      </p>
                      <p className="text-sm text-gray-500">Credit Enhancement Report</p>
                    </div>
                    <span className="font-semibold text-gray-900">
                      ${tiers.find(t => t.tier_id === `tier_${selectedTier}`)?.price}
                    </span>
                  </div>
                  
                  {selectedAddons.map(addonId => {
                    const addon = addons.find(a => a.addon_id === addonId);
                    return (
                      <div key={addonId} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">{addon?.name}</span>
                        <span className="text-gray-900">${addon?.price}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Total */}
              <div className="p-6 bg-gray-50">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-[#012169]">${calculateTotal()}</span>
                </div>
              </div>
            </div>
            
            {/* Payment Button */}
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setStep(3)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                data-testid="pay-order-button"
                onClick={handleSubmit}
                disabled={submitting}
                className="px-8 py-4 bg-[#E31837] hover:bg-[#c4142f] text-white rounded-lg font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <>Processing...</>
                ) : (
                  <>
                    <DollarSign className="w-5 h-5" />
                    Pay ${calculateTotal()} & Order Report
                  </>
                )}
              </button>
            </div>
            
            {/* Security Note */}
            <p className="text-center text-sm text-gray-500 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              Secure payment powered by Stripe
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PASReportOrder;
